import { FormAssociated, SlhCoreFormElement } from "@slhuicore/base";
import { html, LitElement, TemplateResult } from "lit";
import {
  customElement,
  property,
  query,
  state,
  queryAssignedNodes,
} from "lit/decorators.js";
import { IconDefaultType as IconType } from "@slhuiicon/icon";
import { Translatable } from "@slhui/i18n/src/mixins/translatable";
import { DateService as dateService } from "@slhuicore/utils/date.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { ClassInfo, classMap } from "lit/directives/class-map.js";
import { SlhFormIcons } from "@slhui/form-icons";
import * as Datepicker from "./datepicker.constants";

import { style } from "./assets/datepicker.generated-style";
import {
  DatepickerDay,
  PopupPosition,
  DEFAULT_DATE_FORMAT,
} from "./datepicker.type";

/**
 * SLH component to create a datepicker.
 *
 * @CustomElement slh-datepicker
 *
 * @cssprop --slh-default-font-family - Datepicker font family.
 * @cssprop --slh-base-m-font-size - Datepicker month font size.
 * @cssprop --slh-base-xs-font-size - Datepicker font size. Datepicker days font size.
 * @cssprop --slh-base-xxs-font-size - Datepicker week days font size. Datepicker hint font size.
 * @cssprop --slh-base-semi-bold-font-weight - Datepicker font weight.
 * @cssprop --slh-base-xxs-line-height - Datepicker days line height. Line height. Datepicker hint line height.
 * @cssprop --slh-color-neutral - Datepicker arrows color. Datepicker font color and hover placeholder color.
 * @cssprop --slh-color-neutral-shade-00 - Datepicker input background color. Calendar background color.
 * @cssprop --slh-color-neutral-shade-10 - Disabled datepicker input background color.
 * @cssprop --slh-color-neutral-shade-20 - Datepicker day hover background color. Datepicker current date background color. Datepicker border color. Disabled datepicker input icon color (fill).
 * @cssprop --slh-color-neutral-shade-40 - Datepicker calendar other month days font color. Input placeholder text color. Datepicker icon color (fill). Datepicker clear icon color (fill). Datepicker input hover border color. Disabled datepicker input font color.
 * @cssprop --slh-color-neutral-shade-60 - Datepicker calendar arrows hover color. Week days color.
 * @cssprop --slh-color-neutral-shade-80 - Hover datepicker font color and calendar month color.
 * @cssprop --slh-color-primary-shade-20 - Active datepicker background color.
 * @cssprop --slh-color-primary-shade-60 - Active datepicker hover border color. Datepicker hint color.
 * @cssprop --slh-color-error - Error datepicker state label color.
 * @cssprop --slh-color-error-shade-20 - Error datepicker state background color.
 * @cssprop --slh-color-error-shade-60 - Error datepicker state border color.
 * @cssprop --slh-color-error-shade-80 - Error datepicker state hober border color.
 * @cssprop --slh-color-error-shade-40 - Drop shadow color for focused error state datepicker input.
 * @cssprop --slh-color-primary-shade-60 - Focused datepicker drop shadow.
 * @cssprop --slh-base-bold-font-weight - Datepicker hint font weight.
 *
 * @slot default - Datepicker label. Designed to display datepicker label (slh-label component).
 * @slot hint - Custom content below the datepicker, designed to contain recommendations for user input.
 */
@customElement("slh-datepicker")
export default class SlhDatepicker
  extends Translatable(FormAssociated(LitElement))
  implements SlhCoreFormElement
{
  static styles = style;

  @query("input", false)
  private inputElement!: HTMLInputElement;

  @query(".datepicker-input-wrapper", false)
  private containerElement!: HTMLDivElement;

  @queryAssignedNodes({ slot: "hint" })
  protected hintSlotNodes: Array<HTMLElement> | undefined;

  @query(".hint-icon-container", false)
  private hintIconContainer!: HTMLElement;

  /**
   * First day of week
   */
  @property({
    type: Boolean,
    attribute: "sunday-first",
    reflect: true,
  })
  sundayFirst: boolean = false;

  /**
   * Format of the date that should be displayed
   */
  @property({
    type: String,
    attribute: "date-format",
    reflect: true,
  })
  dateFormat: string = DEFAULT_DATE_FORMAT;

  /**
   * Assign value to the input field
   */
  @property({
    type: String,
    attribute: true,
    reflect: true,
  })
  value: string = "";

  /**
   * Assign placeholder text to the input field
   */
  @property({
    type: String,
    attribute: true,
    reflect: true,
  })
  placeholder?: string;

  /**
   * Disables the datepicker
   */
  @property({
    type: Boolean,
    attribute: true,
    reflect: true,
  })
  disabled: boolean = false;

  /**
   * Sets the datepicker as non-editable
   */
  @property({
    type: Boolean,
    attribute: true,
    reflect: true,
  })
  readonly: boolean = false;

  /**
   * Renders datepicker in error state
   */
  @property({
    type: Boolean,
    attribute: true,
    reflect: true,
  })
  error: boolean = false;

  /**
   * Popup position
   */
  @property({
    type: String,
    attribute: "popup-position",
    reflect: true,
  })
  popupPosition: PopupPosition = PopupPosition.auto;

  @state()
  protected selectedDate: string | null = null;

  @state()
  protected labelState = "";

  @state()
  protected datepickerWeekDay: number = 0;

  @state()
  protected openState: boolean = false;

  @state()
  protected hasHint: boolean = false;

  private month: number;

  private year: number;

  private localDate: Date = new Date();

  /*
   * Current date in UTC
   */
  private todayUTCDate: Date = new Date(
    Date.UTC(
      this.localDate.getUTCFullYear(),
      this.localDate.getUTCMonth(),
      this.localDate.getUTCDate(),
      0,
      0,
      0,
      0
    )
  );

  /*
   * Selected date in UTC
   */
  private dateUTC: Date = new Date(
    Date.UTC(
      this.localDate.getUTCFullYear(),
      this.localDate.getUTCMonth(),
      this.localDate.getUTCDate(),
      0,
      0,
      0,
      0
    )
  );

  private openTop: boolean = false;

  onOutsideClick: (this: Document, e: MouseEvent) => any = () => {};

  onHostFocus: (e: Event) => any = () => {};

  onHostBlur: (e: Event) => any = () => {};

  constructor() {
    super();
    this.month = this.dateUTC.getMonth();
    this.year = this.dateUTC.getFullYear();
  }

  connectedCallback() {
    super.connectedCallback();
    this.onOutsideClick = this.outsideClick.bind(this);
    this.classList.add("datepicker-wrapper");
    this.setState(
      this.value !== "" ? this.value : this.todayUTCDate.toISOString()
    );
    this.onHostFocus = this.hostFocus.bind(this);
    this.onHostBlur = this.hostBlur.bind(this);
    this.addEventListener("focus", this.onHostFocus);
    this.addEventListener("blur", this.onHostBlur);
  }

  disconnectedCallback() {
    document.removeEventListener("click", this.onOutsideClick);
    this.removeEventListener("focus", this.onHostFocus);
    this.removeEventListener("blur", this.onHostBlur);
    super.disconnectedCallback();
  }

  /**
   * Close datepicker on outside click
   *
   * @param {MouseEvent} event
   */
  protected outsideClick(event: MouseEvent) {
    if (!event.composedPath().includes(this.renderRoot)) {
      this.close();
    }
  }

  protected selectDay(date: Date) {
    this.setState(date.toISOString());
    this.emitEvent(Datepicker.EVENT_DATEPICKER_CHANGE);
  }

  /**
   * @returns {ClassInfo}
   */
  renderClasses(): ClassInfo {
    return {
      open: this.openState,
      "open-top": this.openTop,
    };
  }

  /**
   * @returns {ClassInfo}
   */
  renderInputClasses(): ClassInfo {
    return {
      active: this.openState,
    };
  }

  render(): TemplateResult | void {
    return html`
      <slot></slot>
      ${this.renderInputElement()}
      <div class="datepicker-container ${classMap(this.renderClasses())}">
        <div class="date-holder">
          ${this.renderPrevMonth()}
          <div class="date">${this.getMonthName(this.month)} ${this.year}</div>
          ${this.renderNextMonth()}
        </div>
        <div id="datepicker">${this.datepicker()}</div>
      </div>
      ${this.renderHint()}
    `;
  }

  /**
   * Render HTML input element
   * @returns {TemplateResult}
   */
  private renderInputElement(): TemplateResult {
    return html`<div
      @click="${this.open}"
      class="datepicker-input-wrapper ${classMap(this.renderInputClasses())}"
      ?disabled="${this.disabled}"
      ?readonly="${this.readonly}"
      ?error="${this.error}"
    >
      <input
        type="text"
        name="${ifDefined(this.name)}"
        placeholder="${ifDefined(this.placeholder)}"
        value="${this.getFormattedState()}"
        ?disabled="${this.disabled}"
        ?readonly="${this.readonly}"
        @focus="${this.focus}"
        @blur="${this.blur}"
        @change="${() =>
          this.setStateAndDispatchEvent(Datepicker.EVENT_DATEPICKER_CHANGE)}"
        @input="${() =>
          this.setStateAndDispatchEvent(Datepicker.EVENT_DATEPICKER_INPUT)}"
      />
      <slh-icon type="${IconType.ICO_DATEPICKER}"></slh-icon>
    </div>`;
  }

  protected getFormattedState(): string {
    return dateService.formatDate(this.selectedDate as string, this.dateFormat);
  }

  /**
   * @returns {TemplateResult}
   */
  protected renderPrevMonth(): TemplateResult {
    return html`<div @click="${this.prevMonth}" class="prev-month">
      <slh-icon type="${IconType.ICO_ARROW_OPEN_LEFT}"></slh-icon>
      </dev>`;
  }

  /**
   * @returns {TemplateResult}
   */
  protected renderNextMonth(): TemplateResult {
    return html`<div @click="${this.nextMonth}" class="prev-month">
      <slh-icon type="${IconType.ICO_ARROW_OPEN_RIGHT}"></slh-icon>
      </dev>`;
  }

  /**
   * Requests an update which is processed asynchronously.
   *
   * @param name name of requesting property
   * @param oldValue old value of requesting property
   */
  requestUpdate(name?: PropertyKey, oldValue?: unknown) {
    if (name === "value" && oldValue !== this.value) {
      this.setState(this.value);
    }
    return super.requestUpdate(name, oldValue);
  }

  firstUpdated() {
    this.setA11y();
    this.updateFormValue();
    this.updateValidity();
    window.setTimeout(() => {
      this.calculatePosition();
    }, 1);
  }

  /**
   * @param {string} value
   */
  private setState(value: string): void {
    try {
      const date = dateService.parseDate(value);

      if (this.validDate(date)) {
        const dateUTC = new Date(
          Date.UTC(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            0,
            0,
            0,
            0
          )
        );
        this.selectedDate = dateUTC.toISOString();
        this.setAriaValue();
        this.updateInputValue();
        this.setDateStates(dateUTC);
        this.updateFormValue();
        this.updateValidity();
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn("datepicker error:", error);
    }
  }

  /**
   * @param {string} value
   */
  private setStateFromFormatedValue(value: string): void {
    try {
      if (value.length === this.dateFormat.length) {
        const date: Date = dateService.parse(value, this.dateFormat);
        if (this.validDate(date)) {
          const dateUTC = new Date(
            Date.UTC(
              date.getFullYear(),
              date.getMonth(),
              date.getDate(),
              0,
              0,
              0,
              0
            )
          );
          this.selectedDate = dateUTC.toISOString();
          this.setAriaValue();
          this.updateInputValue();
          this.setDateStates(dateUTC);
          this.updateFormValue();
          this.updateValidity();
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn("datepicker error:", error);
    }
  }

  protected updateInputValue(): void {
    if (this.inputElement && this.selectedDate) {
      this.inputElement.value = this.getFormattedState();
    }
  }

  // eslint-disable-next-line class-methods-use-this
  protected validDate(date: Date): boolean {
    if (date && date.getFullYear().toString().length === 4) {
      return true;
    }
    return false;
  }

  protected setDateStates(date: Date): void {
    this.dateUTC = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    );
    this.month = date.getMonth();
    this.year = date.getFullYear();
    this.requestUpdate();
  }

  /**
   * @param {string} eventName
   */
  private setStateAndDispatchEvent(eventName: string) {
    if (this.isEditable()) {
      this.setStateFromFormatedValue(this.inputElement?.value);
      this.emitEvent(eventName);
    }
  }

  /**
   * @param {string} eventName
   *
   * @fires "input"
   * @fires "clear"
   * @fires "change"
   */
  private emitEvent(eventName: string) {
    this.value = this.selectedDate ? this.selectedDate : "";
    const event = new CustomEvent(eventName, {
      bubbles: true,
      composed: true,
      detail: {
        value: this.value,
        name: this.name,
      },
    });
    this.dispatchEvent(event);
  }

  /**
   * Language change event handler
   */
  protected resetTranslations() {
    this.requestUpdate();
  }

  protected getMonthName(month: number): string {
    const monthsLabels = [
      this.trans("calendar.months.jan"),
      this.trans("calendar.months.feb"),
      this.trans("calendar.months.mar"),
      this.trans("calendar.months.apr"),
      this.trans("calendar.months.may"),
      this.trans("calendar.months.jun"),
      this.trans("calendar.months.jul"),
      this.trans("calendar.months.aug"),
      this.trans("calendar.months.sep"),
      this.trans("calendar.months.oct"),
      this.trans("calendar.months.nov"),
      this.trans("calendar.months.dec"),
    ];
    return monthsLabels[month];
  }

  protected getDaysLabels(): Array<string> {
    const sunday = [this.trans("calendar.weekdays-short.su")];
    const days = [
      this.trans("calendar.weekdays-short.mo"),
      this.trans("calendar.weekdays-short.tu"),
      this.trans("calendar.weekdays-short.we"),
      this.trans("calendar.weekdays-short.th"),
      this.trans("calendar.weekdays-short.fr"),
      this.trans("calendar.weekdays-short.sa"),
    ];
    if (this.sundayFirst) {
      return sunday.concat(days);
    }
    return days.concat(sunday);
  }

  protected getDayLabel(day: number): string {
    return this.getDaysLabels()[day];
  }

  /**
   * Generate datepicker table
   * @returns TemplateResult
   */
  protected datepicker(): TemplateResult {
    // datepicker content
    return html`<table class="datepicker-table">
      ${this.renderWeekDays()} ${this.renderDays()}
    </table>`;
  }

  protected renderDays(): TemplateResult {
    const datepickerDays: Array<DatepickerDay> = this.getDatepickerDays();
    const weeksplitted: Array<Array<DatepickerDay>> =
      this.getDaysByWeek(datepickerDays);

    return html`${weeksplitted.map(
      (week: Array<DatepickerDay>) =>
        html`<tr class="week">
          ${week.map(
            (object: DatepickerDay) =>
              html`<td
                @click="${this.selectDay.bind(this, object.date)}"
                class="day ${object.class}"
              >
                <span class="${object.class}">${object.date.getDate()}</span>
              </td>`
          )}
        </tr>`
    )}`;
  }

  // eslint-disable-next-line class-methods-use-this
  protected getDaysByWeek(
    datepickerDays: Array<DatepickerDay>
  ): Array<Array<DatepickerDay>> {
    const weeksplitted: Array<Array<DatepickerDay>> = [];
    let days: Array<DatepickerDay> = [];

    datepickerDays.forEach((object: DatepickerDay, key: number) => {
      days.push(object);
      if (key % 7 === 6 && days.length > 0) {
        weeksplitted.push(days);
        days = [];
      }
    });

    return weeksplitted;
  }

  // eslint-disable-next-line class-methods-use-this
  protected renderWeekDays(): TemplateResult {
    return html`<tr class="week-days">
      ${Array.from(Array(Datepicker.WEEK_DAYS_LENGHT).keys()).map(
        (i: number) => html`<td class="day">${this.getDayLabel(i)}</td>`
      )}
    </tr>`;
  }

  protected getDatepickerDays(): Array<DatepickerDay> {
    const days: Array<DatepickerDay> = [];

    const month: number = this.dateUTC.getMonth();
    const year: number = this.dateUTC.getFullYear();
    const daysInMonth = this.getDaysInMonth(month, year);
    const firstDayDate = new Date(Date.UTC(year, month, 1));
    const jan = 0;
    const dec = 11;
    const prevMonth = month === jan ? dec : month - 1;
    const prevYear = prevMonth === dec ? year - 1 : year;
    const prevDays = this.getDaysInMonth(prevMonth, prevYear);

    let nextDaysDate = 1;
    let currentDate = 1;
    let prevMonthIncrementer = 0;
    let prevMonthDayIncrementer = 1;
    let firstDayWeekday: number = firstDayDate.getDay();

    // dates loop
    if (!this.sundayFirst && firstDayWeekday === 0) {
      firstDayWeekday = 7;
    }
    if (!this.sundayFirst) {
      prevMonthIncrementer = 1;
      prevMonthDayIncrementer = 2;
    }

    for (let i = 0; i < Datepicker.CALENDAR_DAYS_LENGHT; i += 1) {
      if (i + prevMonthIncrementer < firstDayWeekday) {
        // previous month's day
        const dayDate = new Date(
          Date.UTC(
            this.dateUTC.getFullYear(),
            this.dateUTC.getMonth() - 1,
            prevDays - firstDayWeekday + i + prevMonthDayIncrementer
          )
        );
        const datepickerDay: DatepickerDay = {
          class: "other-month",
          date: dayDate,
        };
        days.push(datepickerDay);
      } else if (currentDate > daysInMonth) {
        // next month's day
        const dayDate = new Date(
          Date.UTC(
            this.dateUTC.getFullYear(),
            this.dateUTC.getMonth() + 1,
            nextDaysDate
          )
        );
        const datepickerDay: DatepickerDay = {
          class: "other-month",
          date: dayDate,
        };
        days.push(datepickerDay);
        nextDaysDate += 1;
      } else {
        // current month
        const dayDate = new Date(
          Date.UTC(
            this.dateUTC.getFullYear(),
            this.dateUTC.getMonth(),
            currentDate
          )
        );
        const datepickerDay: DatepickerDay = {
          class: this.dayClasses(dayDate),
          date: dayDate,
        };
        days.push(datepickerDay);
        currentDate += 1;
      }
    }

    return days;
  }

  protected dayClasses(date: Date): string {
    const result: Array<string> = [];
    if (this.todayUTCDate.getTime() === date.getTime()) {
      result.push("today");
    }
    if (
      this.selectedDate &&
      dateService.parseDate(this.selectedDate).getTime() === date.getTime()
    ) {
      result.push("selected");
    }
    return result.join(" ");
  }

  // eslint-disable-next-line class-methods-use-this
  protected getDaysInMonth(month: number, year: number): number {
    // 0 = last day of the previous month
    return new Date(year, month + 1, 0).getDate();
  }

  protected nextMonth(): void {
    if (this.dateUTC.getMonth() === 11) {
      this.dateUTC = new Date(this.dateUTC.getFullYear() + 1, 0, 1);
    } else {
      this.dateUTC = new Date(
        this.dateUTC.getFullYear(),
        this.dateUTC.getMonth() + 1,
        1
      );
    }
    this.month = this.dateUTC.getMonth();
    this.year = this.dateUTC.getFullYear();
    this.requestUpdate();
  }

  protected prevMonth(): void {
    if (this.dateUTC.getMonth() === 0) {
      this.dateUTC = new Date(this.dateUTC.getFullYear() - 1, 11, 1);
    } else {
      this.dateUTC = new Date(
        this.dateUTC.getFullYear(),
        this.dateUTC.getMonth() - 1,
        1
      );
    }
    this.month = this.dateUTC.getMonth();
    this.year = this.dateUTC.getFullYear();
    this.requestUpdate();
  }

  private calculatePosition(): void {
    if (this.popupPosition === PopupPosition.top) {
      this.openTop = true;
    } else if (this.popupPosition === PopupPosition.auto) {
      const { body } = document;
      const { documentElement } = document;

      const height = Math.max(
        body.scrollHeight,
        body.offsetHeight,
        documentElement.clientHeight,
        documentElement.scrollHeight,
        documentElement.offsetHeight
      );

      if (this.offsetTop + Datepicker.DATEPICKER_HEIGHT > height) {
        this.openTop = true;
      }
    }
  }

  /**
   * Open datepicker
   */
  public open() {
    if (this.isEditable()) {
      document.addEventListener("click", this.onOutsideClick);
      this.openState = true;
    }
  }

  /**
   * Close datepicker
   */
  public close() {
    if (!this.disabled) {
      document.removeEventListener("click", this.onOutsideClick);
      this.openState = false;
      if (this.selectedDate) {
        this.updateInputValue();
        this.setDateStates(dateService.parseISO(this.selectedDate));
      }
    }
  }

  /**
   * Check if is set aria-label attribute
   */
  private ariaLabelExists() {
    return String(this.getAttribute("aria-label")) !== "";
  }

  /**
   * Add aria-label attribute
   */
  private setAriaLabel() {
    if (!this.ariaLabelExists()) {
      if (this.labelState !== "") {
        this.internals.ariaLabel = this.labelState;
      }

      if (this.labelState === "" && this.placeholder) {
        this.internals.ariaLabel = this.placeholder;
      }
    } else {
      this.internals.ariaLabel = this.getAttribute("aria-label");
    }
  }

  /**
   * Add aria attribute
   */
  private setA11y() {
    this.setAttribute("tabindex", "0");
    this.internals.role = "textbox";
    this.internals.ariaMultiLine = "false";
    this.internals.ariaReadOnly = `${this.readonly}`;
    this.internals.ariaDisabled = `${this.disabled}`;
    this.setAriaValue();
    this.setAriaLabel();
  }

  private setAriaValue() {
    this.internals.ariaValueText = `${this.selectedDate}`;
  }

  /**
   * @returns {string}
   */
  public getLabel(): string {
    return this.labelState;
  }

  /**
   * @returns {string}
   */
  public getValue(): string {
    if (this.selectedDate) {
      return this.selectedDate;
    }
    return "";
  }

  public getName(): string {
    return this.name;
  }

  /**
   * Returns HTML native element
   * @returns {HTMLInputElement}
   */
  public getNativeElement(): HTMLInputElement {
    const element: HTMLInputElement = this.inputElement.cloneNode(
      true
    ) as HTMLInputElement;
    element.value = this.getValue() as string;
    return element;
  }

  /**
   * @returns Boolean
   */
  public isEditable(): Boolean {
    return !this.disabled && !this.readonly;
  }

  /**
   * Clear HTML input value and sync to state
   */
  public clear(): void {
    if (this.isEditable() && this.inputElement) {
      this.inputElement.value = "";
      this.inputElement.focus();
    }
    this.setStateAndDispatchEvent(Datepicker.EVENT_DATEPICKER_CLEAR);
  }

  /**
   * Apply "focused" css class to the container on input focus
   */
  public focus(): void {
    if (this.isEditable()) {
      this.containerElement.classList.add("focused");
    }
  }

  /**
   * Remove "focused" css class from the container on input blur
   */
  public blur(): void {
    if (this.isEditable()) {
      this.containerElement.classList.remove("focused");
    }
  }

  // eslint-disable-next-line class-methods-use-this
  public hasErrorSlot(): boolean {
    return false;
  }

  /**
   * Render textfield hint
   * @returns {TemplateResult}
   */
  private renderHint(): TemplateResult {
    return html`<div class="hint-wrapper ${classMap(this.renderHintClasses())}">
      <div class="hint-icon-container"></div>
      <slot name="hint" @slotchange="${this.onHintSlotChange}"></slot>
    </div>`;
  }

  private onHintSlotChange(): void {
    this.hasHint = false;
    this.hintSlotNodes?.forEach((hint) => {
      if (hint.textContent?.trim() !== "") {
        this.hasHint = true;
      }
    });
    if (this.hasHint) {
      this.hintIconContainer?.appendChild(this.getHintIcon());
    } else {
      this.hintIconContainer.innerHTML = "";
    }
  }

  // eslint-disable-next-line class-methods-use-this
  private getHintIcon(): HTMLElement {
    return SlhFormIcons.getHintIcon();
  }

  /**
   *
   * @returns {ClassInfo} ClassList
   */
  private renderHintClasses(): ClassInfo {
    return {
      hidden: !this.hasHint,
    };
  }

  private updateFormValue(): void {
    this.internals.setFormValue(this.getValue());
  }

  private updateValidity(): void {
    if (this.required && this.getValue() === "") {
      this.internals.setValidity(
        { valueMissing: true },
        this.trans("form-validation.required")
      );
    } else {
      this.internals.setValidity({});
    }
  }

  private hostFocus(): void {
    this.inputElement.focus();
    this.setAttribute("tabindex", "-1");
  }

  private hostBlur(): void {
    this.setAttribute("tabindex", "0");
  }
}
